import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Gift, Coins, Heart, ShoppingBag, Users, ArrowRight, Sparkles,
  TrendingUp, Wallet,
} from 'lucide-react';
import CustomerAccountLayout from '../../components/CustomerAccountLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ Icon, label, value, sub, href, testid, accent = 'gold' }) => {
  const inner = (
    <div
      data-testid={testid}
      className={`group p-5 sm:p-6 rounded-3xl border transition-all hover:-translate-y-0.5 ${
        accent === 'gold'
          ? 'border-yellow-500/30 bg-gradient-to-br from-yellow-500/[0.06] via-zinc-950 to-black hover:border-yellow-500/50'
          : 'border-white/10 bg-white/[0.02] hover:border-yellow-500/30 hover:bg-white/[0.04]'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          accent === 'gold' ? 'bg-yellow-500/15 border border-yellow-500/40' : 'bg-white/[0.04] border border-white/10'
        }`}>
          <Icon className={`w-4.5 h-4.5 ${accent === 'gold' ? 'text-yellow-300' : 'text-white/65'}`} />
        </span>
        {href && (
          <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-yellow-300 group-hover:translate-x-0.5 transition-all" />
        )}
      </div>
      <div className={`text-[10.5px] uppercase tracking-[0.2em] font-bold ${
        accent === 'gold' ? 'text-yellow-300/85' : 'text-white/45'
      }`}>{label}</div>
      <div className="mt-1 font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl font-semibold tracking-tight text-white tabular-nums">
        {value}
      </div>
      {sub && <div className="mt-1 text-[12.5px] text-white/55">{sub}</div>}
    </div>
  );
  return href ? <Link to={href} className="block">{inner}</Link> : inner;
};

const AccountDashboard = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/customer/account/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const s = data?.summary || {};
  const cust = data?.customer || {};
  const firstName = (cust.name || '').split(' ')[0] || 'there';
  const giftDollars = ((s.gift_credit_cents || 0) / 100).toFixed(2);

  return (
    <CustomerAccountLayout
      currentKey="dashboard"
      title={`Welcome back, ${firstName}`}
      subtitle="Your NoFee account at a glance — rewards, favorites, orders, and more."
    >
      {loading ? (
        <div className="text-white/45 text-[14px]" data-testid="dashboard-loading">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="dashboard-cards">
            <StatCard
              Icon={Sparkles}
              label="Rewards balance"
              value={`${s.total_points || 0}`}
              sub="points earned"
              href="/account/rewards"
              testid="card-rewards-balance"
              accent="gold"
            />
            <StatCard
              Icon={Gift}
              label="Gift credits"
              value={`$${giftDollars}`}
              sub="redeemable at any restaurant"
              href="/account/rewards"
              testid="card-gift-credits"
              accent="gold"
            />
            <StatCard
              Icon={Heart}
              label="Favorite restaurants"
              value={s.favorites_count || 0}
              sub="saved spots"
              href="/account/favorites"
              testid="card-favorites"
            />
            <StatCard
              Icon={ShoppingBag}
              label="Orders placed"
              value={s.orders_count || 0}
              sub="all-time"
              href="/account/orders"
              testid="card-orders"
            />
            <StatCard
              Icon={Users}
              label="Referrals"
              value={s.referrals_count || 0}
              sub={s.referral_earnings_pts ? `+${s.referral_earnings_pts} pts earned` : 'friends invited'}
              href="/account/referrals"
              testid="card-referrals"
            />
            <StatCard
              Icon={Wallet}
              label="Status"
              value={s.unread_notifications ? `${s.unread_notifications} new` : 'All caught up'}
              sub="notifications"
              href="/account/notifications"
              testid="card-notifications"
            />
          </div>

          {/* Quick actions */}
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="dashboard-quick-actions">
            <Link
              to="/restaurants"
              className="group p-5 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-yellow-500/40 transition-all"
              data-testid="quick-action-order"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center">
                  <ShoppingBag className="w-4.5 h-4.5 text-yellow-300" />
                </span>
                <div>
                  <div className="text-[14px] font-semibold text-white">Order now</div>
                  <div className="text-[12px] text-white/55">Browse local restaurants</div>
                </div>
                <ArrowRight className="ml-auto w-4 h-4 text-white/30 group-hover:text-yellow-300" />
              </div>
            </Link>
            <Link
              to="/account/referrals"
              className="group p-5 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-yellow-500/40 transition-all"
              data-testid="quick-action-refer"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center">
                  <Users className="w-4.5 h-4.5 text-yellow-300" />
                </span>
                <div>
                  <div className="text-[14px] font-semibold text-white">Invite a friend</div>
                  <div className="text-[12px] text-white/55">Earn +10 points per signup</div>
                </div>
                <ArrowRight className="ml-auto w-4 h-4 text-white/30 group-hover:text-yellow-300" />
              </div>
            </Link>
            <Link
              to="/account/rewards"
              className="group p-5 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-yellow-500/40 transition-all"
              data-testid="quick-action-redeem"
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center">
                  <Coins className="w-4.5 h-4.5 text-yellow-300" />
                </span>
                <div>
                  <div className="text-[14px] font-semibold text-white">Redeem credits</div>
                  <div className="text-[12px] text-white/55">Use at any participating restaurant</div>
                </div>
                <ArrowRight className="ml-auto w-4 h-4 text-white/30 group-hover:text-yellow-300" />
              </div>
            </Link>
          </div>
        </>
      )}
    </CustomerAccountLayout>
  );
};

export default AccountDashboard;
